import React from "react";
import { toast } from "sonner";
import { ChevronDown, MapPin } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { can } from "../lib/permissions";
import { requestAccountExit } from "../hooks/useUnsavedChanges";
import { Button } from "./ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuLabel, DropdownMenuRadioGroup,
  DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";

// Active site in the header: a pin icon (with the site name on wider screens)
// that opens the list of sites. Unsaved work is checked before switching.
export default function SiteSwitcher() {
  const { user, switchSite } = useAuth();
  const all = can(user, "statistics.multisite") || can(user, "economic.multisite");
  const sites = user?.sites || [];
  const current = user?.active_site_id === "all" ? "Tutte le sedi"
    : sites.find((site) => site.id === user?.active_site_id)?.name || "Sede";
  const label = `Sede attiva: ${current}`;
  const choose = (site) => {
    if (site === user.active_site_id) return;
    requestAccountExit(async () => {
      // The server decides the active site: say so when it keeps another one.
      const accepted = await switchSite(site);
      if (accepted && accepted.active_site_id !== site) toast.error("Sede non disponibile per questo accesso");
    });
  };

  // One site and no overview: nothing to choose, only show where you are.
  if (sites.length + (all ? 1 : 0) < 2) {
    return (
      <span className="inline-flex h-9 items-center gap-1.5 px-2 text-sm text-muted-foreground" title={label} data-testid="site-switcher">
        <MapPin className="h-5 w-5 text-primary" aria-hidden="true" />
        <span className="sr-only sm:not-sr-only sm:max-w-[140px] sm:truncate">{current}</span>
      </span>
    );
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="h-9 gap-1.5 px-2 sm:px-2.5" aria-label={`${label}. Cambia sede`} title={label} data-testid="site-switcher">
          <MapPin className="h-5 w-5 text-primary" aria-hidden="true" />
          <span className="hidden max-w-[140px] truncate text-sm font-medium sm:inline">{current}</span>
          <ChevronDown className="hidden h-3.5 w-3.5 text-muted-foreground sm:block" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">Sede attiva</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuRadioGroup value={user.active_site_id || ""} onValueChange={choose}>
          {all && <DropdownMenuRadioItem value="all" className="min-h-10" data-testid="site-option-all">Tutte le sedi</DropdownMenuRadioItem>}
          {sites.map((site) => (
            <DropdownMenuRadioItem key={site.id} value={site.id} className="min-h-10" data-testid={`site-option-${site.id}`}>{site.name}</DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
